// File: frontend/src/views/private/Upload.js

import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import apiService from '../../services/apiService';
import websocketService from '../../services/websocketService';
import PageHeader from '../../components/layout/PageHeader';
import PageContainer from '../../components/layout/PageContainer';
import FileUploadZone from '../../components/upload/FileUploadZone';
import UploadProgress from '../../components/upload/UploadProgress';
import PolicySummaryCard from '../../components/upload/PolicySummaryCard';
import LoadingSpinner from '../../components/utility/LoadingSpinner';
import { 
  Upload as UploadIcon, FileText, AlertCircle, Loader2,
  MessageSquare, ClipboardList, Receipt 
} from 'lucide-react';
import styles from './Upload.module.css';

const Upload = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // ✅ Guard to prevent re-triggering processDocument
  const hasTriggeredProcessing = useRef(false);
  
  const [uploadState, setUploadState] = useState('idle');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [fileMetadata, setFileMetadata] = useState(null);
  const [extractedMetadata, setExtractedMetadata] = useState(null);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [currentSection, setCurrentSection] = useState(null);
  const [completedSections, setCompletedSections] = useState([]);
  const [totalSections, setTotalSections] = useState(0);
  const [allSections, setAllSections] = useState([]);
  const [isStartingPolicyChat, setIsStartingPolicyChat] = useState(false);
  const [isStartingClaimsChat, setIsStartingClaimsChat] = useState(false);
  const [isStartingQuoteChat, setIsStartingQuoteChat] = useState(false);

  const handleFileSelect = async (file) => {
    if (!file) return;

    if (file.type !== 'application/pdf') {
      setError('Only PDF files are supported');
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      setError('File size must be less than 50MB');
      return;
    }

    setError(null);
    setUploadState('uploading');
    setProgress(5);

    try {
      console.log('📤 [UPLOAD] Starting file upload', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type
      });

      const base64Content = await fileToBase64(file);
      setProgress(10);

      const uploadResponse = await apiService.uploadFile({
        file_name: file.name,
        content_type: file.type,
        file_content: base64Content,
        file_size: file.size
      }, user.user_id);

      if (!uploadResponse.success) {
        throw new Error(uploadResponse.error || 'Upload failed');
      }

      console.log('✅ [UPLOAD] File uploaded successfully', {
        fileId: uploadResponse.data.file_id,
        fileName: uploadResponse.data.file_name
      });

      setUploadedFile(uploadResponse.data);
      setFileMetadata(uploadResponse.data);
      setProgress(15);

      await processDocument(uploadResponse.data.file_id);

    } catch (err) {
      console.error('❌ [UPLOAD] Upload error:', err);
      setError(err.message || 'Failed to upload file');
      setUploadState('error');
      setProgress(0);
    }
  };

  const processDocument = async (fileId) => {
    setUploadState('processing');
    setProgress(18);

    try {
      console.log('🤖 [UPLOAD] Starting chat session for metadata extraction', {
        fileId
      });

      const chatResponse = await apiService.startChat({
        agent_name: 'ai_metadata_extraction',
        agent_version: 'v1'
      }, user.user_id);

      if (!chatResponse.success) {
        throw new Error(chatResponse.error || 'Failed to start chat session');
      }

      const chatId = chatResponse.data.chat_id;
      console.log('✅ [UPLOAD] Chat session created', {
        chatId,
        fileId
      });

      setProgress(20);

      await processViaWebSocket(fileId, chatId);

    } catch (err) {
      console.error('❌ [UPLOAD] Processing error:', err);
      setError(err.message || 'Failed to process document');
      setUploadState('error');
      setProgress(0);
    }
  };

  const processViaWebSocket = (fileId, chatId) => {
    return new Promise((resolve, reject) => {
      let accumulatedContent = '';
      let sectionsExtracted = [];
      
      const onOpen = () => {
        console.log('🔌 [UPLOAD] WebSocket connected', {
          fileId,
          chatId,
          hasTriggered: hasTriggeredProcessing.current,
          timestamp: new Date().toISOString()
        });
        
        setProgress(22);
        
        // ✅ GUARD: Prevent re-triggering on reconnection
        if (hasTriggeredProcessing.current) {
          console.warn('⚠️ [UPLOAD] Processing already triggered, skipping duplicate call', {
            fileId,
            chatId
          });
          return;
        }
        
        hasTriggeredProcessing.current = true;
        console.log('🚀 [UPLOAD] Triggering processDocument', {
          fileId,
          chatId,
          userId: user.user_id
        });
        
        websocketService.executeCommand('processDocument', {
          file_id: fileId,
          chat_id: chatId,
          user_id: user.user_id
        }, user.user_id, user.enterprise_id);
      };

      const onMessage = (data) => {
        // Skip timeout warnings
        if (data.message === 'Endpoint request timed out') {
          return;
        }

        if (data.type === 'stream_started') {
          console.log('🎬 [UPLOAD] Stream started', {
            fileId,
            chatId,
            messageId: data.message_id
          });
          setProgress(25);
          setCurrentSection(null);
          setCompletedSections([]);
          setTotalSections(0);
          setAllSections([]);
        }
        else if (data.type === 'stream_chunk') {
          accumulatedContent += data.chunk;
          
          // Extract sections list (only once)
          if (sectionsExtracted.length === 0) {
            const sectionsArrayMatch = accumulatedContent.match(/"sections_extracted":\s*\[(.*?)\]/s);
            if (sectionsArrayMatch) {
              const sectionsStr = sectionsArrayMatch[1];
              const sections = sectionsStr.match(/"([^"]+)"/g)?.map(s => s.replace(/"/g, '').toUpperCase());
              if (sections && sections.length > 0) {
                sectionsExtracted = sections;
                setTotalSections(sections.length);
                setAllSections(sections);
                console.log('📋 [UPLOAD] Sections detected', {
                  fileId,
                  totalSections: sections.length,
                  sections
                });
              }
            }
          }
          
          // Track section progress
          const sectionMatches = accumulatedContent.match(/"section_marker":\s*"<SECTION:([^>]+)>"/g);
          if (sectionMatches) {
            const detectedSections = sectionMatches.map(match => match.match(/<SECTION:([^>]+)>/)[1]);
            
            if (detectedSections.length > 0) {
              const latestSection = detectedSections[detectedSections.length - 1];
              setCurrentSection(latestSection);
              
              if (detectedSections.length > 1) {
                setCompletedSections(detectedSections.slice(0, -1));
              }
              
              if (sectionsExtracted.length > 0) {
                const sectionProgress = (detectedSections.length / sectionsExtracted.length) * 75;
                const totalProgress = Math.round(20 + sectionProgress);
                setProgress(Math.min(totalProgress, 95));
              }
            }
          }
        }
        else if (data.type === 'stream_completed') {
          console.log('✅ [UPLOAD] Stream completed', {
            fileId,
            chatId,
            hasMetadata: !!data.extracted_metadata,
            tokensUsed: data.tokens_used,
            processingTime: data.processing_time_ms
          });
          
          if (currentSection) {
            setCompletedSections(prev => [...prev, currentSection]);
          }
          setCurrentSection(null);
          
          setExtractedMetadata(data.extracted_metadata);
          setProgress(100);
          setUploadState('completed');
          
          // ✅ RESET GUARD: Allow future uploads
          hasTriggeredProcessing.current = false;
          
          websocketService.disconnect();
          console.log('🔌 [UPLOAD] WebSocket disconnected after completion');
          resolve();
        }
        else if (data.type === 'stream_error') {
          console.error('❌ [UPLOAD] Stream error', {
            fileId,
            chatId,
            error: data.error
          });
          
          // ✅ RESET GUARD: Allow retry
          hasTriggeredProcessing.current = false;
          
          websocketService.disconnect();
          reject(new Error(data.error || 'Processing failed'));
        }
      };

      const onClose = (event) => {
        console.log('🔌 [UPLOAD] WebSocket closed', {
          fileId,
          chatId,
          code: event?.code,
          reason: event?.reason,
          wasClean: event?.wasClean
        });
      };

      const onError = (error) => {
        console.error('❌ [UPLOAD] WebSocket error', {
          fileId,
          chatId,
          error: error.message
        });
        
        // ✅ RESET GUARD: Allow retry
        hasTriggeredProcessing.current = false;
        
        websocketService.disconnect();
        reject(new Error('WebSocket connection failed'));
      };

      console.log('🔌 [UPLOAD] Connecting WebSocket', {
        fileId,
        chatId,
        userId: user.user_id,
        enterpriseId: user.enterprise_id
      });

      websocketService.connect(
        user.user_id,
        user.enterprise_id,
        onOpen,
        onMessage,
        onClose,
        onError,
        false // ✅ Auto-reconnect disabled for upload
      );
    });
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleStartPolicyChat = async () => {
    try {
      setIsStartingPolicyChat(true);
      console.log('🚀 [UPLOAD] Starting new policy chat for file:', fileMetadata.file_id);
      
      const chatResponse = await apiService.startChat({
        agent_name: 'ai_allegoryinsurance_policy_assistant',
        agent_version: 'v1',
        file_id: fileMetadata.file_id
      }, user.user_id);

      console.log('✅ [UPLOAD] Policy chat response:', chatResponse);

      if (!chatResponse.success) {
        throw new Error(chatResponse.error || 'Failed to start chat');
      }

      const newChatId = chatResponse.data.chat_id;
      console.log('✅ [UPLOAD] Policy chat created:', newChatId);

      // ✅ ADD: Navigate with from=upload and fileId parameters
      navigate(`/dashboard/chat/${newChatId}?from=upload&fileId=${fileMetadata.file_id}`);

    } catch (err) {
      console.error('❌ [UPLOAD] Failed to start policy chat:', err);
      alert(err.message || 'Failed to start policy chat');
    } finally {
      setIsStartingPolicyChat(false);
    }
  };

  const handleStartClaimsChat = async () => {
    try {
      setIsStartingClaimsChat(true);
      console.log('🚀 [UPLOAD] Starting new claims chat for file:', fileMetadata.file_id);
      
      const chatResponse = await apiService.startChat({
        agent_name: 'ai_allegoryinsurance_claims_assistant',
        agent_version: 'v1',
        file_id: fileMetadata.file_id
      }, user.user_id);

      console.log('✅ [UPLOAD] Claims chat response:', chatResponse);

      if (!chatResponse.success) {
        throw new Error(chatResponse.error || 'Failed to start chat');
      }

      const newChatId = chatResponse.data.chat_id;
      console.log('✅ [UPLOAD] Claims chat created:', newChatId);

      // ✅ ADD: Navigate with from=upload and fileId parameters
      navigate(`/dashboard/chat/${newChatId}?from=upload&fileId=${fileMetadata.file_id}`);

    } catch (err) {
      console.error('❌ [UPLOAD] Failed to start claims chat:', err);
      alert(err.message || 'Failed to start claims chat');
    } finally {
      setIsStartingClaimsChat(false);
    }
  };

  const handleStartQuoteChat = async () => {
    try {
      setIsStartingQuoteChat(true);
      console.log('🚀 [UPLOAD] Starting new quote chat for file:', fileMetadata.file_id);
      
      const chatResponse = await apiService.startChat({
        agent_name: 'ai_allegoryinsurance_quote_assistant',
        agent_version: 'v1',
        file_id: fileMetadata.file_id
      }, user.user_id);

      console.log('✅ [UPLOAD] Quote chat response:', chatResponse);

      if (!chatResponse.success) {
        throw new Error(chatResponse.error || 'Failed to start chat');
      }

      const newChatId = chatResponse.data.chat_id;
      console.log('✅ [UPLOAD] Quote chat created:', newChatId);

      // ✅ ADD: Navigate with from=upload and fileId parameters
      navigate(`/dashboard/chat/${newChatId}?from=upload&fileId=${fileMetadata.file_id}`);

    } catch (err) {
      console.error('❌ [UPLOAD] Failed to start quote chat:', err);
      alert(err.message || 'Failed to start quote chat');
    } finally {
      setIsStartingQuoteChat(false);
    }
  };

  const handleReset = () => {
    console.log('🔄 [UPLOAD] Resetting upload state');
    
    // ✅ RESET GUARD: Allow new upload
    hasTriggeredProcessing.current = false;
    
    setUploadState('idle');
    setUploadedFile(null);
    setFileMetadata(null);
    setExtractedMetadata(null);
    setError(null);
    setProgress(0);
    setCurrentSection(null);
    setCompletedSections([]);
    setTotalSections(0);
    setAllSections([]);
    setIsStartingPolicyChat(false);
    setIsStartingClaimsChat(false);
    setIsStartingQuoteChat(false);
  };

  return (
    <PageContainer>
      <PageHeader 
        title="Upload Insurance Policy"
        subtitle="Upload your insurance policy PDF and let our AI extract the details automatically"
        icon={<UploadIcon size={24} strokeWidth={2} />}
        showBack={true}
        backTo="/dashboard"
      />

      {uploadState === 'idle' && (
        <FileUploadZone 
          onFileSelect={handleFileSelect}
          error={error}
        />
      )}

      {(uploadState === 'uploading' || uploadState === 'processing') && (
        <UploadProgress 
          state={uploadState}
          progress={progress}
          fileName={uploadedFile?.file_name}
          currentSection={currentSection}
          completedSections={completedSections}
          allSections={allSections}
        />
      )}

      {uploadState === 'completed' && extractedMetadata && (
        <>
          <PolicySummaryCard 
            metadata={extractedMetadata}
            fileInfo={fileMetadata}
            onUploadAnother={handleReset}
          />
          
          <div className={styles.postUploadActions}>
            <h3 className={styles.actionsTitle}>What would you like to do next?</h3>
            <div className={styles.actionsGrid}>
              <button 
                className={styles.actionCard} 
                onClick={handleStartPolicyChat}
                disabled={isStartingPolicyChat}
              >
                <div className={styles.actionIconWrap}>
                  <MessageSquare size={28} strokeWidth={1.5} />
                </div>
                <h4 className={styles.actionTitle}>Ask About Your Policy</h4>
                <p className={styles.actionDescription}>
                  Chat with AI to understand your coverage, limits, and exclusions
                </p>
                <span className={styles.actionButton}>
                  {isStartingPolicyChat ? (
                    <>
                      <Loader2 size={16} strokeWidth={2} className={styles.spinning} />
                      Starting...
                    </>
                  ) : (
                    'Start Policy Chat'
                  )}
                </span>
              </button>
  
              <button 
                className={styles.actionCard} 
                onClick={handleStartClaimsChat}
                disabled={isStartingClaimsChat}
              >
                <div className={styles.actionIconWrap}>
                  <ClipboardList size={28} strokeWidth={1.5} />
                </div>
                <h4 className={styles.actionTitle}>File a Claim</h4>
                <p className={styles.actionDescription}>
                  Get guided assistance to file an insurance claim properly
                </p>
                <span className={styles.actionButton}>
                  {isStartingClaimsChat ? (
                    <>
                      <Loader2 size={16} strokeWidth={2} className={styles.spinning} />
                      Starting...
                    </>
                  ) : (
                    'Claims Assistant'
                  )}
                </span>
              </button>
  
              <button 
                className={styles.actionCard} 
                onClick={handleStartQuoteChat}
                disabled={isStartingQuoteChat}
              >
                <div className={styles.actionIconWrap}>
                  <Receipt size={28} strokeWidth={1.5} />
                </div>
                <h4 className={styles.actionTitle}>Get Smart Quote</h4>
                <p className={styles.actionDescription}>
                  Compare your coverage with AI-powered quotes from multiple insurers
                </p>
                <span className={styles.actionButton}>
                  {isStartingQuoteChat ? (
                    <>
                      <Loader2 size={16} strokeWidth={2} className={styles.spinning} />
                      Starting...
                    </>
                  ) : (
                    'Get a Quote'
                  )}
                </span>
              </button>
            </div>
          </div>
        </>
      )}

      {uploadState === 'error' && (
        <div className={styles.errorContainer}>
          <div className={styles.errorCard}>
            <AlertCircle size={48} className={styles.errorIcon} />
            <h2>Upload Failed</h2>
            <p>{error}</p>
            <button onClick={handleReset} className={styles.retryButton}>
              Try Again
            </button>
          </div>
        </div>
      )}
    </PageContainer>
  );
};

export default Upload;